import React, { Dispatch, SetStateAction, useState } from 'react';
import { CurrentUserGq } from '../../../graphql/users/models/CurrentUserGq';
import { faEdit, faPen, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import VoxelTableCell from '../../../components/voxel/Tables/VoxelTableCell';
import VoxelTableRow from '../../../components/voxel/Tables/VoxelTableRow';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import VoxelTooltipShort from '../../../components/voxel/Tooltip/VoxelTooltipShort';
import VoxelTableHeadCell from '../../../components/voxel/Tables/VoxelTableHeadCell';
import VoxelTable from '../../../components/voxel/Tables/VoxelTable';
import {
  UserTeamRoleGq,
  userCanEditMemberWithRole,
} from '../../../graphql/users/models/UserTeamRoleGq';
import VoxelTablePaginationDataSpecific from '../../../components/voxel/Tables/VoxelTablePaginationDataSpecific';
import { PagingInputGq } from '../../../graphql/users/models/PagingInputGq';
import { PagingResultGq } from '../../../graphql/users/models/PagingResultGq';
import { usersApiService } from '../../../graphql/users/usersApiService';
import { TeamMemberGq } from '../../../graphql/users/models/CurrentTeamUserGq';
import EditTeamRoleDialog from './EditTeamRoleDialog';
import DeleteMemberDialog from './DeleteMemberDialog';
import VoxelAvatar from '../../../components/voxel/VoxelAvatar';

export interface ExistingMembersTableProps {
  currentUser: CurrentUserGq;
  members: TeamMemberGq[];
  paging: PagingInputGq;
  pagingResult: PagingResultGq;
  setPaging: (paging: PagingInputGq) => void;
  reload: () => Promise<unknown>;
}

export const ExistingMembersTable: React.FC<ExistingMembersTableProps> = ({
  members,
  currentUser,
  paging,
  pagingResult,
  setPaging,
  reload,
}) => {
  // Remove member
  const [loadingUserIds, setLoadingUserIds] = useState<number[]>([]);
  const [deleteMemberId, setDeleteMemberId] = useState<number>();
  const deleteMember = members.find((c) => c.id === deleteMemberId);
  const removeUserAcessMutation = usersApiService.mutations.useRemoveUserAcess();
  const removeUser = (memberId: number) => {
    setLoadingUserIds((prev) => [...prev, memberId]);
    removeUserAcessMutation({ memberId: memberId })
      .catch((errorReason) => console.error(errorReason))
      .then(reload)
      .finally(() => setLoadingUserIds((prev) => prev.filter((uid) => uid != memberId)));
  };
  const handleSuccess = (memberId: number) => {
    return reload().then(() => {
      setLoadingUserIds((prev) => prev.filter((uid) => uid != memberId));
    });
  };

  // Edit member
  const [editTeamMember, setEditTeamMember] = useState<TeamMemberGq>(null);
  const ownerCount = members?.filter((u) => u.teamRole === UserTeamRoleGq.OWNER)?.length;

  return (
    <>
      <VoxelTable>
        <thead>
          <VoxelTableRow>
            <VoxelTableHeadCell className="pl-6">User</VoxelTableHeadCell>
            <VoxelTableHeadCell align="center">Invited by</VoxelTableHeadCell>
            <VoxelTableHeadCell align="center">First sign in</VoxelTableHeadCell>
            <VoxelTableHeadCell align="center">Role</VoxelTableHeadCell>
            <VoxelTableCell />
          </VoxelTableRow>
        </thead>

        <tbody>
          {members.map((member) => (
            <VoxelTableRow key={member?.id}>
              <VoxelTableCell className="py-2.5 pl-6 flex flex-row items-center gap-4">
                <VoxelAvatar
                  name={member?.email}
                  img={member?.picture}
                  size='md'
                />
                <div>
                  <VoxelText
                    variant="bread-medium"
                    color="dark"
                    noWrap
                    userSelectClass="select-all"
                  >
                    {member?.givenName + ' ' + member?.familyName}
                  </VoxelText>
                  <VoxelText color="light" noWrap userSelectClass="select-all">
                    {member?.email}
                  </VoxelText>
                </div>
              </VoxelTableCell>
              <VoxelTableCell align="center" className="py-2.5">
                <VoxelAvatar
                  name={member?.invitedBy?.email}
                  img={member?.invitedBy?.picture}
                  size='md'
                  tooltip={
                    <>
                      {member?.invitedBy?.givenName + ' ' + member?.invitedBy?.familyName}
                      <br />
                      {member?.invitedBy?.email}
                    </>
                  }
                />
              </VoxelTableCell>
              <VoxelTableCell align="center" className="py-2.5">
                {!member?.firstSignInTime
                  ? '-'
                  : moment.utc(member?.firstSignInTime).local().format('MMM Do, YY')}
              </VoxelTableCell>
              <VoxelTableCell align="center" className="py-2.5">
                {member.teamRole.toLowerCase()}
              </VoxelTableCell>
              <VoxelTableCell
                align="right"
                className="flex flex-row gap-1 flex-nowrap items-center justify-end"
              >
                <VoxelIconButton
                  tooltip="Edit team role"
                  icon={<FontAwesomeIcon icon={faPen} />}
                  size="xs"
                  color="white"
                  onClick={() => setEditTeamMember(member)}
                  disabled={
                    !userCanEditMemberWithRole(currentUser, member.teamRole) ||
                    loadingUserIds.includes(member.id)
                  }
                />
                <VoxelIconButton
                  tooltip="Remove member"
                  disabled={
                    !(ownerCount > 1 || member.teamRole != UserTeamRoleGq.OWNER) ||
                    (!userCanEditMemberWithRole(currentUser, member.teamRole) &&
                      !(
                        currentUser.teamRole === UserTeamRoleGq.MEMBER &&
                        member.teamRole === UserTeamRoleGq.MEMBER
                      )) ||
                    loadingUserIds.includes(member.id)
                  }
                  onClick={() => setDeleteMemberId(member.id)}
                  size="xs"
                  color="white"
                  icon={<FontAwesomeIcon icon={faTrash} />}
                />
              </VoxelTableCell>
            </VoxelTableRow>
          ))}
          <VoxelTablePaginationDataSpecific
            pagingInput={paging}
            onPagingChange={(event, x) => setPaging(x)}
            pagingResult={pagingResult}
            colSpan={5}
          />
        </tbody>
      </VoxelTable>
      {editTeamMember !== null && (
        <EditTeamRoleDialog
          editTeamMember={editTeamMember}
          close={() => setEditTeamMember(null)}
          onSuccess={() => reload().then(() => setEditTeamMember(null))}
          currentUser={currentUser}
          ownerCount={ownerCount}
        />
      )}
      <DeleteMemberDialog
        member={deleteMember}
        onSuccess={handleSuccess}
        close={() => setDeleteMemberId(undefined)}
      />
    </>
  );
};

export default ExistingMembersTable;
